import { SUFFIX_OPTIONS, REG_EX, GENERAL } from 'utils/constants';

const SUFFIX_ARR = SUFFIX_OPTIONS.map((option) => option.value);

const MIN_AGE = 18;
const MAX_AGE = 120;

const piiFormModel = {
  nameModel: {
    groupId: 'name',
    formFields: {
      first: {
        name: 'first',
        label: 'First name',
        placeholder: '',
        min: 2,
        max: 40,
        // Matches the last-name rules — accepts letters, spaces,
        // hyphens, and apostrophes so entries like "Mary-Anne" or
        // "D’Arcy" clear validation.
        matches: REG_EX.lettersSpacesHyphensApostrophes,
        // Angular parity — `validation/en.yaml:51-54`.
        requiredErrMsg: 'First name is required.',
        minErrMsg: 'First name must be at least 2 characters.',
        maxErrMsg: 'First name cannot be more than 40 characters.',
        matchesErrMsg:
          'First name may only contain letters, spaces, apostrophes, and hyphens.',
      },
      middle: {
        name: 'middle',
        label: 'Middle name',
        placeholder: 'Optional',
        min: 1,
        max: 40,
        matches: REG_EX.lettersSpacesHyphensApostrophes,
        requiredErrMsg: 'Middle name is a required field.',
        minErrMsg: 'Middle name must be at least 1 character.',
        maxErrMsg: 'Middle name must be 40 characters or less.',
        matchesErrMsg:
          'Middle name can only contain letters, spaces, hyphens, and apostrophes.',
      },
      last: {
        name: 'last',
        label: 'Last name',
        placeholder: '',
        min: 2,
        max: 40,
        matches: REG_EX.lettersSpacesHyphensApostrophes,
        // Angular parity — `validation/en.yaml:58-62`.
        requiredErrMsg: 'Last name is required.',
        minErrMsg: 'Last name must be at least 2 characters.',
        maxErrMsg: 'Last name cannot be more than 40 characters.',
        matchesErrMsg:
          'Last name may only contain letters, spaces, apostrophes, and hyphens.',
      },
      suffix: {
        name: 'suffix',
        label: 'Suffix',
        placeholder: 'Select suffix',
        oneOf: SUFFIX_ARR,
        oneOfErrMsg: 'Please provide a valid suffix.',
      },
    },
  },
  addressModel: {
    groupId: 'address',
    formFields: {
      street1: {
        name: 'street1',
        label: 'Street address',
        placeholder: 'Start typing your address',
        min: 3,
        max: 50,
        matches: REG_EX.lettersNumbersSpacesPeriodsHyphensSlashesHashes,
        requiredErrMsg: 'Address is a required field.',
        minErrMsg: 'Too short. Address must be at least 3 characters long.',
        maxErrMsg: 'Too long. Address must be 50 characters or less.',
        matchesErrMsg:
          'Address field can only contain letters, numbers, spaces, hyphens, periods, hashes, and slashes.',
      },
      street2: {
        name: 'street2',
        label: 'Apartment, suite, unit',
        placeholder: 'Optional',
        min: 3,
        max: 50,
        matches: REG_EX.lettersNumbersSpacesHyphensSlashesHashesPeriods,
        minErrMsg:
          'Too short. Your apartment/suite must be at least 3 characters long (e.g. use APT 22 instead of 22).',
        maxErrMsg: 'Too long. Apartment/suite must be 50 characters or less.',
        matchesErrMsg:
          'Apartment/suite can only contain letters, numbers, spaces, periods, hyphens, hashes and slashes.',
      },
      city: {
        name: 'city',
        label: 'City',
        placeholder: '',
        min: 2,
        max: 40,
        matches: REG_EX.lettersSpacesHyphen,
        requiredErrMsg: 'City is a required field.',
        minErrMsg: 'City must be at least 2 characters long.',
        maxErrMsg: 'City must be 40 characters or less.',
        matchesErrMsg: 'City can only contain letters, spaces, and hyphens.',
        nonMatchesErrMsg: 'City cannot begin or end with a hyphen.',
      },
      state: {
        name: 'state',
        label: 'State',
        placeholder: 'Select state',
        requiredErrMsg: 'You must select a state from the drop-down menu.',
        oneOfErrMsg: 'You must select a state from the drop-down menu.',
      },
      zip: {
        name: 'zip',
        label: 'Zip code',
        placeholder: '',
        length: 5,
        matches: REG_EX.numbers,
        requiredErrMsg: 'Zip code is a required field.',
        lengthErrMsg: 'Zip code must be five digits.',
        matchesErrMsg: 'Zip code can only contain numbers.',
      },
      country: {
        name: 'country',
        label: 'Country',
        placeholder: 'Select country',
        requiredErrMsg: 'Country is required.',
      },
    },
  },
  birthDateModel: {
    formFields: {
      birthDate: {
        name: 'birthDate',
        format: {
          format: 'MM/DD/YYYY',
          type: 'mask' as const,
        },
        label: 'Date of birth',
        placeholder: 'MM/DD/YYYY',
        minAge: MIN_AGE,
        maxAge: MAX_AGE,
        maskedValue: '••/••/••••',
        requiredErrMsg: 'Date of birth is required.',
        validErrMsg: 'Please provide a valid date of birth.',
        futureErrMsg: 'Date of birth cannot be in the future.',
        minAgeErrMsg: `You must be at least ${MIN_AGE} years old.`,
        maxAgeErrMsg: `Date of birth cannot be more than ${MAX_AGE} years ago.`,
        // Child validator (see `childBirthDateValidation` in
        // validationSchema.ts). Copy mirrors Angular's `RelatedChild`
        // domain model (`my-account/domain/related-child.ts:23-39`)
        // resolved through `my-account/en.yaml:593-597`.
        invalidErrMsg: 'Please provide a valid date of birth.',
        pastErrMsg: 'Date of birth cannot be in the future.',
        childMaxAge: 18,
        minorErrMsg: 'Only children less than 18 years old can be enrolled.',
      },
    },
  },
  phoneNumberModel: {
    formFields: {
      phoneNumber: {
        name: 'phoneNumber',
        label: 'Phone number',
        placeholder: '(123) 456 7890',
        length: 10,
        mask: GENERAL.phoneNumberMask,
        matches: REG_EX.phoneNumber,
        requiredErrMsg: 'Phone number is a required field.',
        lengthErrMsg: 'Phone number must be 10 digits in length.',
        matchesErrMsg:
          'Phone number is invalid. Please provide a valid phone number.',
      },
    },
  },
  ssnModel: {
    formFields: {
      ssn: {
        name: 'ssn',
        label: 'Social Security Number (SSN)',
        placeholder: '',
        length: 9,
        matches: REG_EX.ssn,
        // Angular parity — `my-account/en.yaml:599-602`. Labels keep
        // the "(SSN)" parenthetical (the input still needs to read
        // "SSN" in the field label per Figma); error messages drop
        // it so the copy reads exactly like Angular's toast.
        requiredErrMsg: 'Social Security Number is required.',
        lengthErrMsg: 'Social Security Number must be 9 digits.',
        matchesErrMsg: 'The entered SSN is not valid.',
      },
      confirmSsn: {
        name: 'confirmSsn',
        label: 'Confirm Social Security Number (SSN)',
        placeholder: '',
        // Angular parity — `my-account/en.yaml:603-604`.
        requiredErrMsg: 'Confirm Social Security Number is required.',
        matchesErrMsg: 'The Social Security Numbers must match.',
      },
    },
  },
  emailAddressModel: {
    formFields: {
      emailAddress: {
        name: 'emailAddress',
        type: 'text',
        label: 'Email address',
        placeholder: '',
        max: 100,
        matches: REG_EX.email,
        // Angular parity — `my-account/en.yaml:586-587`. Both the
        // built-in `.email()` and the strict `.matches()` rule share
        // the same "Email address must be in the format xxx@xxx.xxx."
        // copy so whichever fires first, the visible message matches
        // Angular's `emailAddressPattern`.
        requiredErrMsg: 'Email address is required.',
        emailErrMsg: 'Email address must be in the format xxx@xxx.xxx.',
        maxErrMsg: 'Email address must be less than 100 characters',
        matchesErrMsg: 'Email address must be in the format xxx@xxx.xxx.',
      },
      confirmEmail: {
        name: 'confirmEmailAddress',
        label: 'Confirm email address',
        placeholder: '',
        // Angular parity — `my-account/en.yaml:605-606`. `related-adult
        // .ts:22-26` uses ONLY Required + MustMatch on this field, no
        // email/pattern checks — the confirm value only has to equal
        // the already-validated primary, so React's schema mirrors
        // that shape.
        requiredErrMsg: 'Confirm email address is required.',
        matchesErrMsg: 'The email addresses entered must match.',
      },
    },
  },
  usernameModel: {
    formFields: {
      username: {
        name: 'username',
        label: 'Username',
        placeholder: '',
        min: 6,
        max: 100,
        matches: REG_EX.usernamePattern,
        requiredErrMsg: 'Username is required',
        minErrMsg: 'Username must be at least 6 characters in length.',
        maxErrMsg: 'Username must be 100 characters or less.',
        matchesErrMsg:
          'Your username can only contain letters, numbers, and special characters # $ & +—= _ @. It may not begin or end with a special character, and usernames are not case-sensitive.',
      },
    },
  },
};

export default piiFormModel;
